import React, { useEffect, useState } from 'react';
import { AlertTriangle, RotateCcw, ShieldAlert } from 'lucide-react';
import apiService from '../../services/apiService';

/**
 * B3 post-load reversal (BLOCKER governance). Shown in place of the generic
 * "Reopen for edits" affordance when a package is `loaded` AND the server grants
 * the acting user the `load:unload` permission (central FP&A only).
 *
 * Flow: read load-status (batch id / date / row count / period range) -> require
 * a typed reason + explicit confirm checkbox -> POST unload. The server deletes
 * exactly the recorded batch scope, bumps the scenario version (_R2/_R3), and
 * reopens the submission. Legacy loads without persisted scope are refused by the
 * server with a clear message (surfaced here verbatim).
 */
const ForecastLoadReversalPanel = ({ submissionId, actor, canUnload, onReversed }) => {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    setLoadError(null);
    apiService.fetchForecastLoadStatus(submissionId)
      .then((data) => { if (!cancelled) setStatus(data); })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.response?.data?.error || err.message);
      });
    return () => { cancelled = true; };
  }, [submissionId]);

  if (!canUnload) return null;

  const active = status?.active_version || status?.activeVersion || null;
  const warning = status?.downstream_warning || status?.downstreamWarning || {};
  const reasonValid = reason.trim().length >= 8;

  const submitReversal = async () => {
    setBusy(true);
    setResult(null);
    try {
      const data = await apiService.reverseForecastLoad(submissionId, {
        actor,
        reason: reason.trim(),
        confirm: true
      });
      if (data.status === 'already_reversed') {
        setResult({ tone: 'neutral', message: data.message || 'Already reversed.' });
      } else {
        setResult({
          tone: 'ok',
          message: `Unloaded ${data.deletedRows} rows. New version ${data.newScenarioVersion}. Package reopened.`
        });
        setOpen(false);
        setReason('');
        setConfirm(false);
        if (typeof onReversed === 'function') onReversed(data);
      }
    } catch (err) {
      setResult({ tone: 'error', message: err?.response?.data?.error || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="forecast-reversal-panel">
      <div className="forecast-reversal-head">
        <ShieldAlert size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        <strong>Post-load reversal</strong>
        <span className="forecast-health-chip red" style={{ marginLeft: 8 }}>load:unload required</span>
      </div>

      {loadError && (
        <p className="forecast-health-chip red">{loadError}</p>
      )}

      {active && (
        <div className="forecast-reversal-batch">
          <div>Loaded batch <code>{active.loadBatchId}</code></div>
          <div>Scenario version <code>{active.scenarioVersion}</code></div>
          <div>{active.rowCount} rows · loaded by {active.loadedBy || '—'} · {String(active.loadedAt || '').slice(0, 16).replace('T', ' ')}</div>
          {(active.periodStart || active.periodEnd) && (
            <div>Periods {active.periodStart || '?'} → {active.periodEnd || '?'}</div>
          )}
          {active.reversed && (
            <div className="forecast-health-chip neutral">This batch has already been reversed.</div>
          )}
          {active.hasScope === false && (
            <div className="forecast-health-chip red">
              <AlertTriangle size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              Legacy load — no persisted batch scope. Reversal will be refused; delete under manual FP&amp;A governance.
            </div>
          )}
        </div>
      )}

      <div className="forecast-reversal-warning">
        <AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
        {warning.message || 'This removes loaded forecast rows from GL_Fact and supersedes the loaded version.'}
      </div>

      {!open ? (
        <button
          type="button"
          className="forecast-secondary-button"
          disabled={busy || (active && active.reversed)}
          onClick={() => { setOpen(true); setResult(null); }}
        >
          <RotateCcw size={15} /> Reverse load
        </button>
      ) : (
        <div className="forecast-reversal-confirm">
          <label className="forecast-reversal-label" htmlFor="reversal-reason">
            Typed reason (required, ≥ 8 chars)
          </label>
          <textarea
            id="reversal-reason"
            rows={2}
            placeholder="e.g. Material correction after GL load; reload under corrected version."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <label className="forecast-reversal-ack">
            <input
              type="checkbox"
              checked={confirm}
              onChange={(event) => setConfirm(event.target.checked)}
            />
            I acknowledge this deletes the loaded rows from GL_Fact and supersedes the version. This cannot be undone.
          </label>
          <div className="forecast-decision-actions">
            <button
              type="button"
              className="forecast-primary-button"
              disabled={busy || !reasonValid || !confirm}
              onClick={submitReversal}
            >
              <RotateCcw size={15} /> Confirm reversal
            </button>
            <button
              type="button"
              className="forecast-secondary-button"
              disabled={busy}
              onClick={() => { setOpen(false); setReason(''); setConfirm(false); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {result && (
        <span className={`forecast-health-chip ${result.tone === 'ok' ? 'green' : result.tone === 'error' ? 'red' : 'neutral'}`}>
          {result.message}
        </span>
      )}
    </section>
  );
};

export default ForecastLoadReversalPanel;
